import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../api/axios";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Plus,
  AlertCircle,
  Download,
  Paperclip,
  Mail,
  Save,
  Eye,
  X,
} from "lucide-react";
import { formatCurrencyCOP } from "../../utils/format";
import LoadingOverlay from "../../components/LoadingOverlay";

function InvoicePayments() {
  const { id } = useParams(); // invoiceId
  const [invoice, setInvoice] = useState(null);
  const [supplierEmail, setSupplierEmail] = useState("");
  const [updatingSupplierEmail, setUpdatingSupplierEmail] = useState(false);
  const [showVoidAllModal, setShowVoidAllModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState("");
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [isReceiptDragging, setIsReceiptDragging] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [submittingPaymentWithEmail, setSubmittingPaymentWithEmail] =
    useState(false);
  const [resendingPaymentId, setResendingPaymentId] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: "",
    payment_date: "",
    payment_method: "",
    observacion: "",
    sendEmail: false,
  });
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!receiptFile) {
      setReceiptPreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(receiptFile);
    setReceiptPreviewUrl(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [receiptFile]);

  const fetchInvoice = async () => {
    try {
      setLoadingInvoice(true);
      const res = await api.get(`/invoices/${id}`);
      setInvoice(res.data);
      setSupplierEmail(res.data.supplier?.email || "");
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar la factura");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoadingPayments(true);
      const res = await api.get(`/payments/${id}`);
      setPayments(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar los pagos");
    } finally {
      setLoadingPayments(false);
    }
  };

  function formatNumberWithDots(value) {
    if (!value) return "";
    return parseInt(value, 10).toLocaleString("es-CO");
  }

  const handleReceiptFile = (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("El comprobante no puede superar los 10 MB");
      setReceiptFile(null);
      return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    const hasValidType = !file.type || allowedTypes.includes(file.type);
    const hasValidExtension = /\.(pdf|jpe?g|png)$/i.test(file.name);
    if (!hasValidType || !hasValidExtension) {
      toast.error("El comprobante debe ser PDF, JPG o PNG");
      setReceiptFile(null);
      return;
    }

    setReceiptFile(file);
  };

  const handleUpdateSupplierEmail = async () => {
    if (!invoice.supplier?.id) {
      toast.error("No se encontró el proveedor de esta factura");
      return;
    }

    const email = supplierEmail.trim().toLowerCase();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Ingrese un correo electrónico válido");
      return;
    }

    try {
      setUpdatingSupplierEmail(true);
      const response = await api.put(`/suppliers/${invoice.supplier.id}`, {
        email: email || null,
      });
      const updatedEmail = response.data.supplier?.email || "";
      setSupplierEmail(updatedEmail);
      setInvoice((currentInvoice) => ({
        ...currentInvoice,
        supplier: {
          ...currentInvoice.supplier,
          email: updatedEmail,
        },
      }));
      toast.success("Correo del proveedor actualizado correctamente");
    } catch (error) {
      toast.error(
        error.response?.data?.errors?.[0]?.msg ||
          error.response?.data?.details ||
          error.response?.data?.error ||
          "No se pudo actualizar el correo del proveedor",
      );
    } finally {
      setUpdatingSupplierEmail(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    const today = new Date().toISOString().split("T");
    if (newPayment.payment_date > today) {
      toast.error("La fecha de pago no puede ser mayor al día de hoy");
      return;
    }

    // 🔹 Validación monto
    if (!newPayment.amount || Number(newPayment.amount) <= 0) {
      toast.error("Ingrese un monto válido mayor a 0");
      return;
    }

    // 🔹 Validación fecha
    if (!newPayment.payment_date) {
      toast.error("Ingrese la fecha del pago");
      return;
    }

    // 🔹 Validación método
    if (!newPayment.payment_method) {
      toast.error("Seleccione un método de pago");
      return;
    }

    // 🚫 No permitir pagar más del saldo
    if (Number(newPayment.amount) > saldo) {
      toast.error("El monto no puede ser mayor al saldo pendiente");
      return;
    }

    // ✅ Preparar payload (observación opcional)
    const payload = new FormData();
    payload.append("amount", newPayment.amount);
    payload.append("payment_date", newPayment.payment_date);
    payload.append("payment_method", newPayment.payment_method);
    payload.append("observacion", newPayment.observacion?.trim() || "");
    payload.append("sendEmail", String(newPayment.sendEmail));
    if (receiptFile) {
      payload.append("receipt", receiptFile);
    }

    try {
      setSubmittingPayment(true);
      setSubmittingPaymentWithEmail(newPayment.sendEmail);
      const res = await api.post(`/payments/${id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.emailSent || res.data.emailSkipped) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }

      // 🔹 Reset limpio
      setNewPayment({
        amount: "",
        payment_date: "",
        payment_method: "",
        observacion: "",
        sendEmail: false,
      });
      setReceiptFile(null);
      setShowReceiptPreview(false);
      e.target.reset();

      await fetchInvoice();
      await fetchPayments();
    } catch (err) {
      if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else if (err.response?.data?.details) {
        toast.error(err.response.data.details);
      } else {
        toast.error("Error al registrar el pago");
      }
    } finally {
      setSubmittingPayment(false);
      setSubmittingPaymentWithEmail(false);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    try {
      const response = await api.get(`/payments/${payment.id}/receipt`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = payment.receipt_original_name || "comprobante";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo descargar el comprobante");
    }
  };

  const handleResendReceipt = async (payment) => {
    setResendingPaymentId(payment.id);
    try {
      const response = await api.post(`/payments/${payment.id}/resend-receipt`);
      toast.success(response.data.message);
      await fetchPayments();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.error || "No se pudo reenviar el comprobante",
      );
    } finally {
      setResendingPaymentId(null);
    }
  };

  const handleVoid = async (paymentId) => {
    if (!window.confirm("¿Seguro que deseas anular este pago?")) return;

    try {
      await api.patch(`/payments/${paymentId}/void`);
      toast.success("Pago anulado correctamente");
      await fetchInvoice();
      await fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error al anular el pago");
    }
  };

  const handleVoidAllPayments = async () => {
    setShowVoidAllModal(false);

    try {
      // Llamada a la nueva ruta única
      await api.patch(`/payments/${id}/void-all`);

      toast.success("Factura restablecida correctamente");
      await fetchInvoice();
      await fetchPayments();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Error al revertir pagos");
    }
  };

  // ✅ Total pagado (solo visual)
  const totalPagado = payments.reduce((sum, p) => {
    if (p.status === "voided") return sum;
    return sum + Number(p.amount || 0);
  }, 0);

  // ✅ Valor REAL a pagar (neto contable)
  const valorAPagar = invoice
    ? Number(invoice.total || 0) -
      Number(invoice.retention || 0) -
      Number(invoice.rete_ica || 0) -
      Number(invoice.discount || 0) -
      Number(
        (invoice.glosa_total != null ? invoice.glosa_total : invoice.glosa) ||
          0,
      )
    : 0;

  // ✅ Saldo REAL (calculado en backend)
  const saldo = invoice ? Number(invoice.balance || 0) : 0;

  // helper opcional
  const fmt = (n) => Number(n || 0).toFixed(2);

  if (!invoice) {
    // Mientras se carga la factura desde la API
    return <LoadingOverlay loading={true} />;
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Pagos de Factura</h2>

        <Link
          to="/invoices"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft size={18} /> Volver
        </Link>
      </div>

      {invoice && (
        <div className="mb-6 bg-gray-50 p-6 rounded-lg shadow-inner grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Proveedor + N° Factura */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Proveedor */}
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Proveedor
              </label>
              <input
                type="text"
                value={invoice.supplier?.name || "—"}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            {/* Número de Factura */}
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                N° Factura
              </label>
              <input
                type="text"
                value={invoice.invoice_number}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            {/* Correo del proveedor */}
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Correo del proveedor
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={supplierEmail}
                  onChange={(e) => setSupplierEmail(e.target.value)}
                  placeholder="Correo del proveedor"
                  className="min-w-0 flex-1 border rounded px-3 py-2"
                />
                <button
                  type="button"
                  onClick={handleUpdateSupplierEmail}
                  disabled={
                    updatingSupplierEmail ||
                    supplierEmail.trim().toLowerCase() ===
                      (invoice.supplier?.email || "").trim().toLowerCase()
                  }
                  className="inline-flex shrink-0 items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  <Save size={16} />
                  Actualizar correo
                </button>
              </div>
            </div>
          </div>

          {/* Subtotal */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Subtotal
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.subtotal)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* IVA */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">IVA</label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.iva)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Flete */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Flete
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.freight)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Concepto Adicional */}
          {Number(invoice.extra_value || 0) > 0 && (
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-700 mb-1">
                  Concepto Adicional
                </label>
                <input
                  type="text"
                  value={invoice.extra_concept || ""}
                  readOnly
                  className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-700 mb-1">
                  Valor Adicional
                </label>
                <input
                  type="text"
                  value={formatCurrencyCOP(invoice.extra_value)}
                  readOnly
                  className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
          )}

          {/* Retención */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Retención
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.retention)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Retención ICA */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Retención ICA
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.rete_ica || 0)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Descuento */}
          {Number(invoice.discount) > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Descuento
              </label>
              <input
                type="text"
                value={formatCurrencyCOP(invoice.discount)}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed "
              />
            </div>
          )}

          {/* Glosa */}
          {Number(
            (invoice.glosa_total != null
              ? invoice.glosa_total
              : invoice.glosa) || 0,
          ) > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Glosa Total
              </label>
              <input
                type="text"
                value={formatCurrencyCOP(
                  invoice.glosa_total != null
                    ? invoice.glosa_total
                    : invoice.glosa,
                )}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>
          )}

          {Number(invoice.glosa_retencion || 0) > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Glosa Retención
              </label>
              <input
                type="text"
                value={formatCurrencyCOP(invoice.glosa_retencion)}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>
          )}

          {Number(invoice.glosa_reteica || 0) > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Glosa Rete ICA
              </label>
              <input
                type="text"
                value={formatCurrencyCOP(invoice.glosa_reteica)}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
              />
            </div>
          )}

          {/* Total */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Total Factura
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(invoice.total)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Valor a pagar */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Valor a pagar
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(valorAPagar)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Pagado */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Pagado
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(totalPagado)}
              readOnly
              className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Saldo */}
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">
              Saldo
            </label>
            <input
              type="text"
              value={formatCurrencyCOP(saldo)}
              readOnly
              className={`border rounded px-3 py-2 bg-gray-100 cursor-not-allowed font-bold ${
                saldo > 0 ? "text-red-600" : "text-green-600"
              }`}
            />
          </div>

          {/* Observación */}
          {invoice?.observacion && (
            <div className="lg:col-span-3 flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                Observación
              </label>
              <textarea
                value={invoice.observacion}
                readOnly
                rows={2}
                className="border rounded px-3 py-2 bg-gray-100 cursor-not-allowed resize-none text-gray-700"
              />
            </div>
          )}
        </div>
      )}

      {/* Formulario para nuevo pago */}
      <form
        onSubmit={handlePaymentSubmit}
        className="bg-gray-100 p-4 rounded-lg mb-6 flex flex-wrap items-center gap-4"
      >
        {/* Monto */}
        <input
          type="text"
          placeholder="Monto"
          value={formatNumberWithDots(newPayment.amount)}
          onChange={(e) => {
            let value = e.target.value.replace(/\./g, "");
            value = value.replace(/[^0-9]/g, "");

            setNewPayment({
              ...newPayment,
              amount: value,
            });
          }}
          className={`h-10 min-w-[160px] p-2 border rounded flex-1 ${
            Number(newPayment.amount) > saldo ? "border-red-500 bg-red-100" : ""
          }`}
          required
        />

        {/* Fecha */}
        <input
          type="date"
          value={newPayment.payment_date}
          onChange={(e) =>
            setNewPayment({ ...newPayment, payment_date: e.target.value })
          }
          className="h-10 min-w-[160px] p-2 border rounded flex-1"
          required
        />

        {/* Método de pago como select */}
        <select
          value={newPayment.payment_method}
          onChange={(e) =>
            setNewPayment({ ...newPayment, payment_method: e.target.value })
          }
          className="h-10 min-w-[180px] p-2 border rounded flex-1"
          required
        >
          <option value="">Seleccione Método</option>
          <option value="Davivienda Ahorros">Davivienda Ahorros</option>
          <option value="Davivienda Corriente">Davivienda Corriente</option>
          <option value="Bancolombia Ahorros">Bancolombia Ahorros</option>
          <option value="Bancolombia Corriente">Bancolombia Corriente</option>
        </select>

        {/* Observación */}
        <input
          type="text"
          placeholder="Observación (opcional)"
          value={newPayment.observacion || ""}
          onChange={(e) =>
            setNewPayment({ ...newPayment, observacion: e.target.value })
          }
          className="h-10 min-w-[180px] p-2 border rounded flex-1"
        />

        <div
          onDragEnter={(e) => {
            e.preventDefault();
            setIsReceiptDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(e) => {
            if (
              !e.relatedTarget ||
              !e.currentTarget.contains(e.relatedTarget)
            ) {
              setIsReceiptDragging(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsReceiptDragging(false);
            handleReceiptFile(e.dataTransfer.files?.[0]);
          }}
          className={`h-10 min-w-[230px] flex flex-1 items-center gap-2 overflow-hidden rounded border px-2 text-sm ${
            isReceiptDragging
              ? "border-blue-500 bg-blue-50 text-blue-700"
              : "bg-white text-gray-600"
          }`}
        >
          <label className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 overflow-hidden px-1 whitespace-nowrap">
            <Paperclip size={16} className="shrink-0" />
            <span className="truncate">
              {receiptFile
                ? receiptFile.name
                : isReceiptDragging
                  ? "Suelta el archivo aquí"
                  : "Arrastra o haz clic para adjuntar"}
            </span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => {
                handleReceiptFile(e.target.files?.[0]);
                e.target.value = "";
              }}
              className="sr-only"
            />
          </label>
          {receiptFile && (
            <>
              <button
                type="button"
                onClick={() => setShowReceiptPreview(true)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                title="Vista previa del comprobante"
                aria-label="Ver comprobante adjunto"
              >
                <Eye size={17} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReceiptPreview(false);
                  setReceiptFile(null);
                }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-red-600 hover:bg-red-50 hover:text-red-800"
                title="Quitar comprobante"
                aria-label="Quitar comprobante adjunto"
              >
                <X size={18} />
              </button>
            </>
          )}
        </div>

        <label
          className="inline-flex min-h-10 flex-none cursor-pointer items-center gap-2 px-1 text-sm text-gray-700"
          title={
            !invoice.supplier?.email?.trim()
              ? "El proveedor no tiene correo registrado"
              : "Enviar notificación al proveedor"
          }
        >
          <input
            type="checkbox"
            checked={newPayment.sendEmail}
            disabled={!invoice.supplier?.email?.trim()}
            onChange={(e) =>
              setNewPayment({ ...newPayment, sendEmail: e.target.checked })
            }
            className="h-3.5 w-3.5 accent-blue-600"
          />
          <span>Enviar</span>
        </label>

        {/* Botón */}
        <button
          type="submit"
          disabled={saldo <= 0 || submittingPayment}
          className={`h-10 px-4 rounded-lg shadow flex items-center gap-2
          ${
            saldo <= 0 || submittingPayment
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white"
          }`}
        >
          <Plus size={16} />{" "}
          {submittingPayment ? "Registrando..." : "Registrar Pago"}
        </button>
      </form>
      {/* Tabla de pagos */}
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase">
              <th className="px-4 py-2">Fecha</th>
              <th className="px-4 py-2">Monto</th>
              <th className="px-4 py-2">Método</th>
              <th className="px-4 py-2">Comprobante</th>

              {/* 🔹 Mostrar columna solo si existe alguna observación */}
              {payments.some((p) => p.observacion) && (
                <th className="px-4 py-2">Observación</th>
              )}

              <th className="px-4 py-2">Estado</th>
              <th className="px-4 py-2">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {loadingPayments ? (
              <tr>
                <td
                  colSpan={payments.some((p) => p.observacion) ? 7 : 6}
                  className="py-4 text-gray-400"
                >
                  Cargando pagos...
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td
                  colSpan={payments.some((p) => p.observacion) ? 7 : 6}
                  className="py-4 text-gray-400 italic"
                >
                  No hay pagos registrados
                </td>
              </tr>
            ) : (
              payments.map((p) => (
                <tr
                  key={p.id}
                  className={`border-t ${
                    p.status === "voided" ? "bg-red-50 opacity-70" : ""
                  }`}
                >
                  {/* Fecha */}
                  <td className="px-4 py-2">
                    {p.payment_date
                      ? p.payment_date.split("-").reverse().join("/")
                      : "-"}
                  </td>

                  {/* Monto */}
                  <td className="px-4 py-2">
                    {p.status === "voided" ? (
                      <span className="line-through text-red-500">
                        {formatCurrencyCOP(p.amount)}
                      </span>
                    ) : (
                      formatCurrencyCOP(p.amount)
                    )}
                  </td>

                  {/* Método */}
                  <td className="px-4 py-2">{p.payment_method}</td>

                  <td className="px-4 py-2">
                    {p.receipt_original_name ? (
                      <div className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleDownloadReceipt(p)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          title="Descargar comprobante"
                        >
                          <Download size={16} />
                          Comprobante
                        </button>
                        <span
                          className={`text-xs ${p.receipt_email_sent_at ? "text-green-600" : "text-orange-600"}`}
                        >
                          {p.receipt_email_sent_at
                            ? "Correo enviado"
                            : "Correo no enviado"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-400">
                          Sin comprobante
                        </span>
                        <span
                          className={`text-xs ${p.receipt_email_sent_at ? "text-green-600" : "text-orange-600"}`}
                        >
                          {p.receipt_email_sent_at
                            ? "Correo enviado"
                            : "Correo no enviado"}
                        </span>
                      </div>
                    )}
                  </td>

                  {/* Observación */}
                  {payments.some((pay) => pay.observacion) && (
                    <td className="px-4 py-2 text-gray-600 italic">
                      {p.observacion || "-"}
                    </td>
                  )}

                  {/* Estado */}
                  <td className="px-4 py-2">
                    {p.status === "active" ? (
                      <span className="text-green-600 font-semibold">
                        Activo
                      </span>
                    ) : (
                      <span className="text-red-500 font-semibold">
                        Anulado
                      </span>
                    )}
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-2">
                    <div className="flex justify-center items-center gap-2">
                      {p.status === "active" && p.receipt_original_name && (
                        <button
                          type="button"
                          onClick={() => handleResendReceipt(p)}
                          disabled={resendingPaymentId === p.id}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white p-2 rounded-lg shadow"
                          title="Reenviar comprobante por correo"
                        >
                          <Mail size={16} />
                        </button>
                      )}
                      {p.status === "active" && invoice.status !== "paid" && (
                        <button
                          onClick={() => handleVoid(p.id)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow"
                          title="Anular pago"
                        >
                          <ArrowLeft size={16} />
                        </button>
                      )}
                      {!(p.status === "active" && p.receipt_original_name) &&
                        !(
                          p.status === "active" && invoice.status !== "paid"
                        ) && (
                          <span className="text-xs text-gray-400 italic">
                            —
                          </span>
                        )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* MODAL DE CONFIRMACIÓN PARA REVERTIR PAGOS */}
      {showVoidAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full mx-4 transform transition-all scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                ¿Revertir todos los pagos?
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Se anularán{" "}
                <strong>
                  {payments.filter((p) => p.status === "active").length}
                </strong>{" "}
                pagos activos. La factura volverá a estado pendiente y permitirá
                edición.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowVoidAllModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
              >
                No, cancelar
              </button>
              <button
                onClick={handleVoidAllPayments}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition shadow-lg shadow-orange-200"
              >
                Sí, revertir
              </button>
            </div>
          </div>
        </div>
      )}

      {submittingPayment && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label="Enviando comprobante"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
            <h3 className="mt-5 text-lg font-bold text-gray-900">
              {submittingPaymentWithEmail
                ? "Enviando notificación"
                : "Registrando pago"}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {submittingPaymentWithEmail
                ? "Registrando el pago y enviando el correo al proveedor..."
                : "Guardando el pago en el sistema..."}
            </p>
          </div>
        </div>
      )}

      {showReceiptPreview && receiptFile && receiptPreviewUrl && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa del comprobante"
          onClick={() => setShowReceiptPreview(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
              <h3 className="truncate font-semibold text-gray-800">
                {receiptFile.name}
              </h3>
              <button
                type="button"
                onClick={() => setShowReceiptPreview(false)}
                className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Cerrar vista previa"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex min-h-[50vh] items-center justify-center overflow-auto bg-gray-100 p-4">
              {receiptFile.type === "application/pdf" ||
              /\.pdf$/i.test(receiptFile.name) ? (
                <iframe
                  src={receiptPreviewUrl}
                  title={`Vista previa de ${receiptFile.name}`}
                  className="h-[70vh] w-full rounded bg-white"
                />
              ) : (
                <img
                  src={receiptPreviewUrl}
                  alt={`Vista previa de ${receiptFile.name}`}
                  className="max-h-[70vh] max-w-full object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        {/* Solo aparece si el saldo es exactamente 0 Y hay al menos un pago activo */}
        {saldo === 0 && payments.some((p) => p.status === "active") && (
          <button
            onClick={() => setShowVoidAllModal(true)} // <-- Cambiado aquí
            className="flex items-center gap-2 bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-200 px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-all"
          >
            <ArrowLeft size={16} />
            Revertir todos los pagos activos
          </button>
        )}
      </div>
    </div>
  );
}

export default InvoicePayments;
